import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { NAV } from "../lib/content";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled;
  const markSrc = solid ? "/media/logo-mark-navy.png" : "/media/logo-mark-white.png";
  const textColor = solid ? "text-navy" : "text-white";

  const linkClass = ({ isActive }) =>
    `hover-line font-medium uppercase transition-colors duration-300 whitespace-nowrap ${textColor} ${
      isActive ? "opacity-100" : "opacity-70 hover:opacity-100"
    }`;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        solid ? "bg-white/90 backdrop-blur-xl border-b border-gray-100 shadow-[0_6px_30px_-18px_rgba(8,28,58,0.4)]" : "bg-transparent"
      }`}
    >
      {/* Top bar */}
      <div className="max-w-[1440px] mx-auto px-5 md:px-12 flex items-center justify-between h-16 md:h-24">
        {/* Logo */}
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 md:gap-3 shrink-0">
          <img src={markSrc} alt="Next Horizon Builders" className="h-12 md:h-14 w-auto transition-opacity duration-300" />
          <span className={`flex flex-col leading-none ${textColor} transition-colors duration-300`}>
            <span className="font-serif text-lg md:text-2xl tracking-tight">Next Horizon</span>
            <span className="font-sans text-[0.58rem] md:text-[0.68rem] tracking-[0.3em] md:tracking-[0.34em] uppercase opacity-80">Builders</span>
          </span>
        </Link>

        {/* Desktop nav — centered, always visible */}
        <nav className="hidden lg:flex items-center gap-9" data-testid="desktop-nav" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={(s) => `${linkClass(s)} text-[0.78rem] tracking-[0.06em]`}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* CTA — always visible on every device */}
        <Link
          to="/careers"
          data-testid="navbar-cta-join"
          className="shrink-0 inline-flex items-center bg-gold text-white uppercase tracking-[0.16em] md:tracking-[0.2em] font-semibold hover:bg-gold-dark transition-colors duration-300 px-4 py-2.5 text-[0.6rem] md:px-7 md:py-3.5 md:text-[0.72rem]"
        >
          Join Our Team
        </Link>
      </div>

      {/* Tablet / mobile nav — always-visible horizontal row, no hamburger */}
      <nav
        data-testid="mobile-nav"
        aria-label="Primary"
        className={`lg:hidden border-t transition-colors duration-500 ${solid ? "border-gray-100" : "border-white/15"}`}
      >
        <div className="flex items-center md:justify-center gap-x-5 sm:gap-x-7 overflow-x-auto no-scrollbar px-5 md:px-12 py-2.5">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mnav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={(s) => `${linkClass(s)} text-[0.66rem] sm:text-[0.7rem] tracking-[0.1em]`}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </motion.header>
  );
};
